/*!
 * Bombka Wspomnień – motion director
 * Self-running sequences (no scroll/hover/click needed):
 *  - [data-bw-cycle]   synchronized step timelines (showcase, steps, cards, lines)
 *  - [data-bw-emitter] memories that rise out of the glowing bauble
 *  - pauses work that is off-screen or in a background tab (performance only)
 * Plus small utilities: copy code, FAQ easing, sticky buy bar, header state.
 */
(function () {
  'use strict';

  var html = document.documentElement;
  var BW = window.BW || {};
  var S = BW.strings || {};
  var TIER = BW.tier || 'high';
  var REDUCED = TIER === 'reduced';

  /* ---------------------------------------------------------- visibility */
  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      var el = en.target;
      el.__bwVisible = en.isIntersecting;
      el.classList.toggle('is-paused', !en.isIntersecting);
      if (en.isIntersecting) el.classList.add('is-seen');
    });
  }, { rootMargin: '120px 0px' }) : null;

  function watch(el) {
    if (el.__bwWatched) return;
    el.__bwWatched = true;
    el.__bwVisible = true;
    if (io) io.observe(el);
  }

  function isLive(el) {
    return !document.hidden && el.__bwVisible !== false;
  }

  /* ---------------------------------------------------------- cycles */
  function Cycle(el) {
    this.el = el;
    this.interval = Math.max(1200, parseInt(el.getAttribute('data-bw-cycle'), 10) || 4000);
    this.step = 0;
    this.items = el.querySelectorAll('[data-bw-step]');
    this.progress = el.querySelectorAll('[data-bw-cycle-progress]');
    this.elapsed = 0;
    this.last = 0;
    this.apply();
    var self = this;
    this.timer = setInterval(function () { self.tick(); }, 200);
  }

  Cycle.prototype.apply = function () {
    var s = this.step;
    for (var i = 0; i < this.items.length; i++) {
      var it = this.items[i];
      var of = parseInt(it.getAttribute('data-bw-of'), 10) || 1;
      var idx = parseInt(it.getAttribute('data-bw-step'), 10) || 0;
      var active = (s % of) === idx;
      var wasActive = it.classList.contains('is-active');
      it.classList.toggle('is-active', active);
      it.classList.toggle('is-leaving', wasActive && !active);
    }
    this.el.style.setProperty('--bw-step', s);
    this.el.style.setProperty('--bw-interval', this.interval + 'ms');
    for (var p = 0; p < this.progress.length; p++) {
      var bar = this.progress[p];
      bar.classList.remove('is-run');
      void bar.offsetWidth;
      bar.classList.add('is-run');
    }
  };

  Cycle.prototype.tick = function () {
    var now = Date.now();
    if (!this.last) this.last = now;
    var dt = now - this.last;
    this.last = now;
    if (!isLive(this.el) || REDUCED && this.el.hasAttribute('data-bw-reduced-static')) return;
    this.elapsed += dt;
    if (this.elapsed >= this.interval) {
      this.elapsed = 0;
      this.step++;
      this.apply();
    }
  };

  /* ---------------------------------------------------------- emitter */
  function Emitter(el) {
    this.el = el;
    this.box = el.querySelector('[data-bw-emits]');
    var tpl = el.querySelector('template[data-bw-emit-items]');
    this.items = [];
    if (tpl && tpl.content) {
      var kids = tpl.content.children;
      for (var i = 0; i < kids.length; i++) this.items.push(kids[i]);
    }
    this.every = Math.max(900, parseInt(el.getAttribute('data-bw-emit-every'), 10) || 2200);
    this.max = TIER === 'high' ? 6 : TIER === 'mid' ? 4 : 3;
    this.idx = 0;
    this.side = 1;
    if (!this.box || !this.items.length || REDUCED) return;
    var self = this;
    this.timer = setInterval(function () { self.emit(); }, this.every);
    setTimeout(function () { self.emit(); }, 600);
  }

  Emitter.prototype.emit = function () {
    if (!isLive(this.el) || this.box.children.length >= this.max) return;
    var src = this.items[this.idx % this.items.length];
    this.idx++;
    this.side *= -1;
    var wrap = document.createElement('span');
    wrap.className = 'bw-emit';
    var inner = document.createElement('span');
    inner.className = 'bw-emit__in';
    inner.appendChild(src.cloneNode(true));
    wrap.appendChild(inner);
    var w = this.el.clientWidth || 400;
    var dx = this.side * (w * (0.18 + Math.random() * 0.26));
    var dy = -(this.el.clientHeight || 500) * (0.42 + Math.random() * 0.18);
    var dur = 7 + Math.random() * 3;
    wrap.style.setProperty('--dx', dx.toFixed(0) + 'px');
    wrap.style.setProperty('--dy', dy.toFixed(0) + 'px');
    wrap.style.setProperty('--rot', ((Math.random() * 14 - 7) * this.side).toFixed(1) + 'deg');
    wrap.style.setProperty('--dur', dur.toFixed(2) + 's');
    wrap.style.setProperty('--sx', ((Math.random() - 0.5) * 40).toFixed(0) + 'px');
    this.box.appendChild(wrap);
    setTimeout(function () { if (wrap.parentNode) wrap.parentNode.removeChild(wrap); }, dur * 1000 + 300);
  };

  /* ---------------------------------------------------------- utilities */
  function initCopy() {
    document.addEventListener('click', function (e) {
      var b = e.target.closest('[data-bw-copy]');
      if (!b) return;
      var code = b.getAttribute('data-bw-copy');
      var label = b.querySelector('[data-bw-copy-label]') || b;
      var done = function () {
        label.textContent = S.copied || 'OK';
        b.classList.add('is-copied');
        setTimeout(function () { label.textContent = S.copy || code; b.classList.remove('is-copied'); }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done, function () { fallbackCopy(code); done(); });
      } else { fallbackCopy(code); done(); }
    });
  }

  function fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', '');
    ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
  }

  function initFaq(scope) {
    var items = (scope || document).querySelectorAll('details[data-bw-faq]');
    Array.prototype.forEach.call(items, function (d) {
      if (d.__bwFaq) return;
      d.__bwFaq = true;
      var sum = d.querySelector('summary');
      var body = d.querySelector('[data-bw-faq-body]');
      if (!sum || !body || REDUCED || !body.animate) return;
      sum.addEventListener('click', function (e) {
        e.preventDefault();
        if (d.__anim) d.__anim.cancel();
        if (d.open) {
          var h = body.offsetHeight;
          d.classList.add('is-closing');
          d.__anim = body.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 380, easing: 'cubic-bezier(.4,0,.2,1)' });
          d.__anim.onfinish = function () { d.open = false; d.classList.remove('is-closing'); d.__anim = null; };
        } else {
          d.open = true;
          var target = body.offsetHeight;
          d.__anim = body.animate([{ height: '0px', opacity: 0 }, { height: target + 'px', opacity: 1 }], { duration: 480, easing: 'cubic-bezier(.2,.8,.2,1)' });
          d.__anim.onfinish = function () { d.__anim = null; };
        }
      });
    });
  }

  function initSticky() {
    var bar = document.querySelector('[data-bw-sticky]');
    var anchor = document.querySelector('[data-bw-buy-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    var btn = bar.querySelector('button');
    var footer = document.querySelector('.bw-final');
    var anchorOut = false, finalIn = false;
    function update() {
      var show = anchorOut && !finalIn;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (btn) btn.tabIndex = show ? 0 : -1;
    }
    new IntersectionObserver(function (en) {
      // Show whenever the main buy button is not on screen (above or below).
      anchorOut = !en[0].isIntersecting;
      update();
    }).observe(anchor);
    if (footer) new IntersectionObserver(function (en) { finalIn = en[0].isIntersecting; update(); }, { rootMargin: '0px 0px -30% 0px' }).observe(footer);
  }

  function initHeader() {
    var header = document.querySelector('[data-bw-header]');
    if (!header) return;
    var ticking = false;
    function set() {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(set); }
    }, { passive: true });
    set();
  }

  function initVideos(scope) {
    var vids = (scope || document).querySelectorAll('.bw-media--video video');
    Array.prototype.forEach.call(vids, function (v) {
      v.muted = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      v.setAttribute('loop', '');
      v.removeAttribute('controls');
      if (REDUCED) { v.removeAttribute('autoplay'); v.pause(); return; }
      var p = v.play && v.play();
      if (p && p.catch) p.catch(function () {});
    });
  }

  /* ---------------------------------------------------------- boot */
  function boot(scope) {
    var root = scope || document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-bw-anim]'), watch);
    Array.prototype.forEach.call(root.querySelectorAll('[data-bw-cycle]'), function (el) {
      if (el.__bwCycle) return;
      el.__bwCycle = new Cycle(el);
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-bw-emitter]'), function (el) {
      if (el.__bwEmitter) return;
      watch(el);
      el.__bwEmitter = new Emitter(el);
    });
    initFaq(root);
    initVideos(root);
    Array.prototype.forEach.call(root.querySelectorAll('select[data-default]'), function (s) {
      var v = s.getAttribute('data-default');
      if (v) s.value = v;
    });
  }

  function start() {
    boot(document);
    initCopy();
    initSticky();
    initHeader();
    // Intro choreography starts automatically right after first paint.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { html.classList.add('bw-ready'); });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
